/**
 * Persistence, behind one interface, so accounts can replace it without touching a caller.
 *
 * ## Why an interface at all, for something localStorage already does
 *
 * Non-negotiable #7: the core has to be ready for accounts and multiplayer without a rewrite.
 * "Where do the settings live" is exactly the kind of decision that leaks everywhere if it is
 * left implicit — twenty `localStorage.getItem` calls with twenty ad-hoc key strings and twenty
 * different opinions about what a corrupt value means. So there is one interface, one key
 * namespace, and one place that knows JSON is involved. A server-backed implementation later
 * satisfies the same three methods and every caller keeps working.
 *
 * ## Why it is synchronous
 *
 * Because the settings are read during start-up and written on a keypress, and an async storage
 * API would push a promise into the input path for no benefit. A future remote implementation
 * caches locally and writes through in the background — which is what a remote settings store
 * has to do anyway, since the aeroplane cannot wait for a round trip to know what its FOV is.
 *
 * ## What it guarantees
 *
 *   - **It never throws.** Private browsing disables `localStorage` by throwing on access, disk
 *     quotas throw on write, and another tab can leave a half-written value. A settings store
 *     that can take down the simulator on start-up is worse than one that forgets your FOV, so
 *     every operation degrades to "no value" or "not saved".
 *   - **It never returns the wrong shape.** Reads go through a validator supplied by the caller,
 *     and anything that fails it is treated as absent. This is not defensive programming for its
 *     own sake: the stored value may have been written by an older build with a different schema,
 *     and that is a normal event rather than an error.
 *
 * `updateFixed` / `updateFrame`: neither. Called on start-up and when a setting changes.
 */

/** The storage backend. Implemented by {@link LocalStorageService} today, by accounts later. */
export interface StorageService {
  /** The stored value for `key`, or `null` if there is none. */
  read(key: string): string | null;
  /** Store `value` under `key`. Returns false when the write could not be made. */
  write(key: string, value: string): boolean;
  /** Forget `key`. */
  remove(key: string): void;
}

/**
 * Every key OpenSkies persists, in one place.
 *
 * Enumerated rather than composed at the call site so that "what does this app store about me"
 * has an answer you can read, and so that a rename is a compile error rather than a silently
 * orphaned value. Namespaced, because `localStorage` is shared with anything else served from
 * the same origin.
 */
export const STORAGE_KEYS = {
  quality: 'skysim.quality',
  coordinationAssist: 'skysim.assist.coordination',
  settings: 'skysim.settings',
  bindings: 'skysim.bindings',
  lastFlight: 'skysim.lastFlight',
  gunneryRecords: 'skysim.gunnery.records',
  /** Set once the pilot has been taught the take-off. See `ui/firstFlight.ts`. */
  firstFlight: 'skysim.firstFlight',
  /** Best landing per runway. Its own key, not the gunnery book — see `game/landingRecords.ts`. */
  landingRecords: 'skysim.landing.records',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * `localStorage`, with every way it can fail handled.
 *
 * The constructor probes the backing store rather than trusting that `globalThis.localStorage`
 * exists: Safari in private mode provides the object and throws from `setItem`, which means a
 * simple existence check passes and the first save takes the page down.
 */
export class LocalStorageService implements StorageService {
  private readonly backing: Storage | null;

  constructor(storage: Storage | undefined = globalThis.localStorage) {
    this.backing = probe(storage);
  }

  /** False when nothing will persist — the settings UI says so rather than lying about saving. */
  get isPersistent(): boolean {
    return this.backing !== null;
  }

  read(key: string): string | null {
    try {
      return this.backing?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  write(key: string, value: string): boolean {
    try {
      this.backing?.setItem(key, value);
      return this.backing !== null;
    } catch {
      // Quota exceeded, or the store was disabled after the probe succeeded.
      return false;
    }
  }

  remove(key: string): void {
    try {
      this.backing?.removeItem(key);
    } catch {
      // Nothing to do: the value is already unreachable, which is what removal was for.
    }
  }
}

/** A `StorageService` that forgets everything. Tests, and the fallback when storage is refused. */
export class MemoryStorageService implements StorageService {
  private readonly map = new Map<string, string>();

  read(key: string): string | null {
    return this.map.get(key) ?? null;
  }

  write(key: string, value: string): boolean {
    this.map.set(key, value);
    return true;
  }

  remove(key: string): void {
    this.map.delete(key);
  }
}

/**
 * Read a JSON value, validated.
 *
 * `validate` narrows an `unknown` to `T`; anything it rejects is treated as absent. A stored
 * value that no longer matches the schema is a *normal* outcome — it was written by an older
 * build — so it is not logged, not repaired and not thrown about. It is simply not there, and
 * the caller falls back to its default the way it would on a fresh install.
 */
export function readJson<T>(
  storage: StorageService,
  key: string,
  validate: (value: unknown) => value is T,
): T | null {
  const raw = storage.read(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return validate(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Write a JSON value. Returns false when it could not be stored. */
export function writeJson(storage: StorageService, key: string, value: unknown): boolean {
  try {
    return storage.write(key, JSON.stringify(value));
  } catch {
    // A cyclic or otherwise unserialisable value. A caller bug, but not one worth crashing on.
    return false;
  }
}

/** True for a plain object — the precondition every settings validator starts from. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Read a bounded number from a record, or fall back. */
export function numberField(
  record: Record<string, unknown>,
  key: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

/** Read a boolean from a record, or fall back. */
export function booleanField(
  record: Record<string, unknown>,
  key: string,
  fallback: boolean,
): boolean {
  const value = record[key];
  return typeof value === 'boolean' ? value : fallback;
}

/** Read a value constrained to a known set, or fall back. */
export function enumField<T extends string>(
  record: Record<string, unknown>,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = record[key];
  return allowed.find((candidate) => candidate === value) ?? fallback;
}

/**
 * Probe a `Storage` by actually writing to it.
 *
 * Safari's private mode hands out a `localStorage` object whose `setItem` throws, so checking
 * that the object exists is not the same question as whether it works. One write and one removal
 * at start-up settles it, and it is the difference between a graceful "settings will not be
 * remembered" and a page that dies the first time the pilot changes their FOV.
 */
function probe(storage: Storage | undefined): Storage | null {
  if (storage === undefined || storage === null) return null;
  const key = '__skysim_probe__';
  try {
    storage.setItem(key, '1');
    storage.removeItem(key);
    return storage;
  } catch {
    return null;
  }
}
